<template>
  <vs-popup
    :title="$t('Title')"
    :active.sync="activeModal">
    <transition name="zoom-fade">
      <div v-if="activeModal">
        <template v-if="!sent">
          <!-- Show email input for non-authenticated users -->
          <div v-if="!authEmail">
            <div class="text-center mb-6">
              <h4 class="text-xl font-semibold mb-2">Forgot password? 🔒</h4>
              <p class="text-grey-dark">
                Enter your email and we'll send you instructions to reset your password.
              </p>
            </div>

            <div class="mb-4">
              <vs-input
                v-model="manualEmail"
                type="email"
                v-validate="'required|email'"
                data-vv-validate-on="input"
                :name="$tc('$General.Email')"
                :label="$tc('$General.Email')"
                autocomplete="email"
                class="w-full"
                icon-no-border
                icon="icon icon-mail"
                icon-pack="feather"
                :danger="errors.has($tc('$General.Email'))"
                :danger-text="errors.first($tc('$General.Email'))"
                val-icon-danger="clear"/>
            </div>
          </div>

          <!-- Show message for authenticated users -->
          <p v-else class="text-grey-dark mb-4">
            {{ $t('DefaultInfoMsg', { email: authEmail }) }}
          </p>

          <vs-divider/>

          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-base">
            <vs-button
              type="border"
              class="w-full sm:w-auto"
              @click.prevent="activeModal = false">
              {{ $t('$General.Cancel') }}
            </vs-button>

            <vs-button
              class="w-full sm:w-auto"
              :disabled="isResendDisabled || (!authEmail && !isManualEmailValid)"
              @click.prevent="sentForgotPasswordEmail">
              {{ $t('$General.Send') }}
            </vs-button>
          </div>
        </template>
        <template v-else>
          <div class="flex items-start mb-4">
            <feather-icon
              icon="MailIcon"
              svgClasses="h-5 w-5 mr-3 mt-0.5 flex-shrink-0"/>
            <p class="text-grey-dark">
              {{ $t('SentInfoMsg', { email: lastUsedEmail }) }}
              <a
                href="#"
                :class="isResendDisabled ? 'link-disabled' : 'link-cta'"
                @click.prevent="isResendDisabled ? null : sentForgotPasswordEmail">
                {{ resendLinkText }}.
              </a>
            </p>
          </div>

          <vs-divider/>

          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-base">
            <vs-button
              type="border"
              class="w-full sm:w-auto"
              @click.prevent="activeModal = false">
              {{ $t('$General.Close') }}
            </vs-button>

            <vs-button
              class="w-full sm:w-auto"
              :disabled="isResendDisabled"
              @click.prevent="sentForgotPasswordEmail">
              {{ resendButtonText }}
            </vs-button>
          </div>
        </template>
      </div>
    </transition>
  </vs-popup>
</template>

<script>

import AppAlert from '@/views/_components/AppAlert.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'SendForgotPasswordEmailPopup',
  components: {
    AppAlert,
  },
  i18n: {
    messages: {
      en: {
        Title: 'Forgot password',
        DefaultInfoMsg: 'We will send you an email to {email} with a link to recover your password.',
        SentInfoMsg: 'We have sent an email to {email} with a link to recover your password. If you have not received the email, you can',
        ResendNewVerificationEmail: 'Resend here',
        RecoveryEmail: 'Recovery email',
        RecoveryEmailSent: 'You will receive a recover password email.',
        ResendCooldownLink: 'Resend in {seconds}s',
        ResendCooldownButton: 'Resend in {seconds}s',
      },
    },
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeModal: this.value,
      sent: false,
      resendCooldown: 0,
      cooldownTimer: null,
      cooldownDuration: 30, // 30 seconds cooldown
      manualEmail: '',
    };
  },
  computed: {
    ...mapGetters({
      authEmail: 'auth/email',
    }),
    isResendDisabled() {
      return this.resendCooldown > 0;
    },
    resendLinkText() {
      return this.isResendDisabled
        ? this.$t('ResendCooldownLink', { seconds: this.resendCooldown })
        : this.$t('ResendNewVerificationEmail');
    },
    resendButtonText() {
      return this.isResendDisabled
        ? this.$t('ResendCooldownButton', { seconds: this.resendCooldown })
        : this.$t('$General.Resend');
    },
    isManualEmailValid() {
      return !this.errors.has(this.$tc('$General.Email')) && this.manualEmail.length > 0;
    },
    lastUsedEmail() {
      return this.authEmail || this.manualEmail;
    },
  },
  watch: {
    value() {
      this.activeModal = this.value;
    },
    activeModal(val) {
      this.$emit('input', this.activeModal);

      if (!val) {
        this.sent = false;
        this.manualEmail = '';
        this.resetCooldown();
      }
    },
  },
  beforeDestroy() {
    this.resetCooldown();
  },
  methods: {
    ...mapActions({
      recoverPassword: 'auth/recoverPassword',
    }),
    async sentForgotPasswordEmail() {
      if (this.isResendDisabled) {
        return;
      }

      // Validate manual email if user is not authenticated
      if (!this.authEmail) {
        const formIsValid = await this.$validator.validate();
        if (!formIsValid) {
          return;
        }
      }

      const emailToUse = this.authEmail || this.manualEmail;

      this.$vs.loading({ type: 'radius' });
      await this.recoverPassword(emailToUse);
      this.sent = true;
      this.$vs.loading.close();

      this.$showSuccessActionNotification({
        title: this.$t('RecoveryEmail'),
        text: this.$t('RecoveryEmailSent'),
      });

      // Start cooldown after sending
      this.startCooldown();

      // Emit global event for cross-component rate limiting with exact timestamp
      const emailSentTime = Date.now();
      // Clear any existing timestamp first, then set the new one
      localStorage.removeItem('forgotPasswordLastSent');
      localStorage.setItem('forgotPasswordLastSent', emailSentTime.toString());
      window.dispatchEvent(new CustomEvent('forgotPasswordEmailSent', {
        detail: { timestamp: emailSentTime },
      }));
    },
    startCooldown() {
      this.resendCooldown = this.cooldownDuration;
      this.cooldownTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          this.resetCooldown();
        }
      }, 1000);
    },
    resetCooldown() {
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }
      this.resendCooldown = 0;
    },
  },
};
</script>

<style scoped lang="scss">
// No custom styles needed - using system utility classes
</style>
